interface VehicleDiagramProps {
  crossed: boolean
  inhibitory: boolean
  singleSensor?: boolean
  singleMotor?: boolean
  color: string
  label?: string
}

export function VehicleDiagram({
  crossed,
  inhibitory,
  singleSensor = false,
  singleMotor = false,
  color,
  label,
}: VehicleDiagramProps) {
  const w = 150
  const h = 140
  const sensorY = 24
  const motorY = 110
  const leftX = 64
  const rightX = 112
  const centerX = 88
  const partLabelX = 46

  const strokeColor = 'var(--color-text-secondary)'
  const mutedColor = 'var(--color-text-muted)'
  const dashArray = inhibitory ? '4,3' : undefined
  const sign = inhibitory ? '\u2212' : '+'

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={w}
      height={h}
      style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
      aria-label={`Wiring diagram for vehicle ${label ?? ''}`}
    >
      {/* Label */}
      {label && (
        <text
          x={centerX}
          y={12}
          textAnchor="middle"
          fill={mutedColor}
          style={{
            fontFamily: 'var(--font-ui)',
            fontSize: '11px',
            fontWeight: 500,
          }}
        >
          {label}
        </text>
      )}

      {/* Sensors */}
      {singleSensor ? (
        <circle
          cx={centerX}
          cy={sensorY}
          r={6}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
        />
      ) : (
        <>
          <circle
            cx={leftX}
            cy={sensorY}
            r={6}
            fill="none"
            stroke={strokeColor}
            strokeWidth={1.5}
          />
          <circle
            cx={rightX}
            cy={sensorY}
            r={6}
            fill="none"
            stroke={strokeColor}
            strokeWidth={1.5}
          />
        </>
      )}

      {/* Motors */}
      {singleMotor ? (
        <rect
          x={centerX - 8}
          y={motorY - 5}
          width={16}
          height={10}
          fill={color}
          opacity={0.6}
          rx={2}
        />
      ) : (
        <>
          <rect
            x={leftX - 8}
            y={motorY - 5}
            width={16}
            height={10}
            fill={color}
            opacity={0.6}
            rx={2}
          />
          <rect
            x={rightX - 8}
            y={motorY - 5}
            width={16}
            height={10}
            fill={color}
            opacity={0.6}
            rx={2}
          />
        </>
      )}

      {/* Connection lines */}
      {singleSensor && singleMotor ? (
        // V1: center sensor → center motor
        <line
          x1={centerX}
          y1={sensorY + 6}
          x2={centerX}
          y2={motorY - 5}
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeDasharray={dashArray}
        />
      ) : singleSensor ? (
        // Single sensor → left motor
        <line
          x1={centerX}
          y1={sensorY + 6}
          x2={leftX}
          y2={motorY - 5}
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeDasharray={dashArray}
        />
      ) : crossed ? (
        // Crossed wiring
        <>
          <line
            x1={leftX}
            y1={sensorY + 6}
            x2={rightX}
            y2={motorY - 5}
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeDasharray={dashArray}
          />
          <line
            x1={rightX}
            y1={sensorY + 6}
            x2={leftX}
            y2={motorY - 5}
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeDasharray={dashArray}
          />
        </>
      ) : (
        // Uncrossed wiring
        <>
          <line
            x1={leftX}
            y1={sensorY + 6}
            x2={leftX}
            y2={motorY - 5}
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeDasharray={dashArray}
          />
          <line
            x1={rightX}
            y1={sensorY + 6}
            x2={rightX}
            y2={motorY - 5}
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeDasharray={dashArray}
          />
        </>
      )}

      {/* +/- labels */}
      {!singleSensor && (
        <>
          <text
            x={leftX - 16}
            y={sensorY + 27}
            textAnchor="middle"
            fill={mutedColor}
            style={{ fontFamily: 'var(--font-ui)', fontSize: '10px' }}
          >
            {sign}
          </text>
          <text
            x={rightX + 16}
            y={sensorY + 27}
            textAnchor="middle"
            fill={mutedColor}
            style={{ fontFamily: 'var(--font-ui)', fontSize: '10px' }}
          >
            {sign}
          </text>
        </>
      )}

      {/* S / M labels */}
      <text
        x={partLabelX}
        y={sensorY + 3}
        textAnchor="end"
        fill={mutedColor}
        style={{ fontFamily: 'var(--font-ui)', fontSize: '9px' }}
      >
        {singleSensor ? 'sensor' : 'sensors'}
      </text>
      <text
        x={partLabelX}
        y={motorY + 3}
        textAnchor="end"
        fill={mutedColor}
        style={{ fontFamily: 'var(--font-ui)', fontSize: '9px' }}
      >
        {singleMotor ? 'motor' : 'motors'}
      </text>
    </svg>
  )
}
